import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Copy, X, Save, Zap, MessageSquare, Hash } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const VARIABLES = [
  { key: '{{musteri_adi}}', label: 'Müşteri Adı', desc: 'Müşterinin tam adı' },
  { key: '{{ticket_no}}', label: 'Ticket No', desc: 'Talep numarası' },
  { key: '{{ticket_konu}}', label: 'Ticket Konusu', desc: 'Talep başlığı' },
  { key: '{{personel_adi}}', label: 'Personel Adı', desc: 'Yanıtlayan personel adı' },
  { key: '{{tarih}}', label: 'Bugünün Tarihi', desc: 'Şu anki tarih' },
  { key: '{{firma_adi}}', label: 'Firma Adı', desc: 'Müşteri firma adı' },
  { key: '{{kategori}}', label: 'Kategori', desc: 'Ticket kategorisi' },
  { key: '{{oncelik}}', label: 'Öncelik', desc: 'Talep önceliği' },
];

const CATEGORIES = ['Genel', 'Karşılama', 'Bilgi Talebi', 'Uzak Bağlantı', 'Sorun Çözümü', 'Kapanış'];

export default function CannedResponsesPage() {
  const [responses, setResponses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', category: '', shortcut: '' });
  const [filter, setFilter] = useState('');
  const [previewContent, setPreviewContent] = useState('');

  useEffect(() => { loadResponses(); }, []);

  const loadResponses = async () => {
    try { const res = await api.get('/canned-responses'); setResponses(res.data); } catch (err) {}
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`/canned-responses/${editId}`, form);
        toast.success('Şablon güncellendi');
      } else {
        await api.post('/canned-responses', form);
        toast.success('Şablon oluşturuldu');
      }
      setShowForm(false); setEditId(null);
      setForm({ title: '', content: '', category: '', shortcut: '' });
      loadResponses();
    } catch (err) { toast.error(err.response?.data?.error || 'Hata'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Şablonu silmek istediğinize emin misiniz?')) return;
    await api.delete(`/canned-responses/${id}`);
    toast.success('Silindi'); loadResponses();
  };

  const handleEdit = (r) => {
    setEditId(r.Id); setForm({ title: r.Title, content: r.Content, category: r.Category || '', shortcut: r.Shortcut || '' });
    setShowForm(true);
  };

  const insertVariable = (varKey) => {
    setForm(prev => ({ ...prev, content: prev.content + varKey }));
  };

  const resolvePreview = (content) => {
    return content
      .replace(/\{\{musteri_adi\}\}/g, 'Ahmet Yılmaz')
      .replace(/\{\{ticket_no\}\}/g, '#1234')
      .replace(/\{\{ticket_konu\}\}/g, 'E-Fatura sorunu')
      .replace(/\{\{personel_adi\}\}/g, 'Destek Personeli')
      .replace(/\{\{tarih\}\}/g, new Date().toLocaleDateString('tr-TR'))
      .replace(/\{\{firma_adi\}\}/g, 'ABC Şirketi')
      .replace(/\{\{kategori\}\}/g, 'Logo Tiger / Go')
      .replace(/\{\{oncelik\}\}/g, 'Normal');
  };

  const filtered = responses.filter(r => {
    if (filter && r.Category !== filter) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Hazır Yanıt Şablonları</h1>
          <p className="text-sm text-slate-500 mt-1">Parametrik değişkenlerle dinamik şablonlar oluşturun</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditId(null); setForm({ title: '', content: '', category: '', shortcut: '' }); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 shadow-lg shadow-blue-600/20">
          <Plus size={16} /> Yeni Şablon
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter('')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${!filter ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>Tümü ({responses.length})</button>
        {CATEGORIES.map(c => {
          const cnt = responses.filter(r => r.Category === c).length;
          if (cnt === 0) return null;
          return <button key={c} onClick={() => setFilter(c)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === c ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>{c} ({cnt})</button>;
        })}
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(r => (
          <div key={r.Id} className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare size={14} className="text-blue-500" />
                  <h3 className="text-sm font-semibold text-slate-800">{r.Title}</h3>
                </div>
                {r.Category && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{r.Category}</span>}
                {r.Shortcut && <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full ml-1">/{r.Shortcut}</span>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => handleEdit(r)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 size={14} /></button>
                <button onClick={() => handleDelete(r.Id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
              </div>
            </div>
            <p className="text-xs text-slate-500 line-clamp-3 whitespace-pre-wrap">{r.Content}</p>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-400">{r.UsageCount} kez kullanıldı</span>
              <button onClick={() => { navigator.clipboard.writeText(r.Content); toast.info('Kopyalandı!'); }}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Copy size={12} /> Kopyala</button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="col-span-full text-center text-slate-400 py-10">Henüz şablon oluşturulmamış</p>}
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-slate-800">{editId ? 'Şablonu Düzenle' : 'Yeni Şablon'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Şablon Adı *</label>
                  <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Hoş geldiniz yanıtı" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Kategori</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Seçiniz</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Kısayol Kodu</label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 text-sm">/</span>
                  <input type="text" value={form.shortcut} onChange={e => setForm({ ...form, shortcut: e.target.value })}
                    className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="hosgeldin" />
                </div>
                <p className="text-xs text-slate-400 mt-1">Mesaj yazarken /hosgeldin yazarak hızlıca kullanabilirsiniz</p>
              </div>

              {/* Variables */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Zap size={14} className="inline text-amber-500" /> Dinamik Değişkenler (tıklayarak ekleyin)
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {VARIABLES.map(v => (
                    <button key={v.key} type="button" onClick={() => insertVariable(v.key)}
                      className="px-2.5 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg text-xs font-mono hover:bg-amber-100 transition-colors" title={v.desc}>
                      {v.key}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Şablon İçeriği *</label>
                <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={6} required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm resize-none"
                  placeholder="Merhaba {{musteri_adi}},&#10;&#10;{{ticket_no}} numaralı talebinizi aldık..." />
              </div>

              {/* Preview */}
              {form.content && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-500 mb-2">📋 Önizleme (örnek verilerle):</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{resolvePreview(form.content)}</p>
                </div>
              )}

              <button type="submit"
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 flex items-center justify-center gap-2">
                <Save size={18} /> {editId ? 'Güncelle' : 'Oluştur'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
